import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { authResetPassword } from '@/shared/api/generated/auth/auth';
import { ApiError, NetworkError } from '@/shared/api/http';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import styles from '@/pages/LoginPage/LoginPage.module.scss';

const schema = z
  .object({
    password: z.string().min(8, t.login.passwordTooShort),
    again: z.string(),
  })
  .refine((v) => v.password === v.again, { message: t.profile.passwordsDiffer, path: ['again'] });

type FormValues = z.infer<typeof schema>;

/** Страница из письма: /reset/<токен>. Токен одноразовый, живёт час. */
export function ResetPage() {
  const { token } = useParams({ from: '/public/reset/$token' });
  const navigate = useNavigate();
  const [expired, setExpired] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', again: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await authResetPassword({ token, password: values.password });
      toast.success(t.reset.done);
      await navigate({ to: '/login' });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setExpired(true);

        return;
      }

      const message =
        cause instanceof ApiError
          ? cause.detail
          : cause instanceof NetworkError
            ? t.common.networkError
            : t.common.unknownError;

      form.setError('password', { message });
    }
  });

  if (expired) {
    return (
      <div className={styles['card']}>
        <header className={styles['head']}>
          <h1 className={styles['title']}>{t.reset.expired}</h1>
          <p className={styles['subtitle']}>{t.forgot.subtitle}</p>
        </header>
        <Button asChild iconRight={<ArrowRight />}>
          <Link to="/forgot">{t.reset.request}</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.form
      className={styles['card']}
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springs.spring}
    >
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.reset.title}</h1>
        <p className={styles['subtitle']}>{t.reset.subtitle}</p>
      </header>

      <Field label={t.reset.password} required error={form.formState.errors.password?.message}>
        <Input
          type="password"
          autoComplete="new-password"
          autoFocus
          {...form.register('password')}
        />
      </Field>

      <Field label={t.reset.again} required error={form.formState.errors.again?.message}>
        <Input type="password" autoComplete="new-password" {...form.register('again')} />
      </Field>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={form.formState.isSubmitting}
        iconRight={<ArrowRight />}
      >
        {t.reset.submit}
      </Button>
    </motion.form>
  );
}
